import { useState, useEffect } from "react";
import logo from "./assets/donex-logo.svg";
import { supabase } from "./lib/supabase";
import { toast, Toaster } from "sonner";
import { Input, Button } from "@donex/ui";
import { InteractiveDemo } from "./InteractiveDemo";

type Status = "idle" | "loading" | "error";

export default function App() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        const appUrl = import.meta.env.VITE_APP_URL ?? "http://localhost:3001";
        window.location.href = appUrl;
      }
    });
  }, []);

  const focusEmail = () => {
    const input = document.getElementById("email-input") as HTMLInputElement | null;
    if (input) {
      input.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => input.focus(), 400);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3002";
      const res = await fetch(`${apiBase}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "cookie" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("Registration failed");

      toast.success("You're on the list!");
      setEmail("");
      setStatus("idle");

      // const { error } = await supabase.auth.signInWithOtp({ email: email.trim() });
      // if (error) throw error;

      // const appUrl = import.meta.env.VITE_APP_URL ?? "http://localhost:3001";
      // window.location.href = `${appUrl}?email=${encodeURIComponent(email.trim())}`;
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="relative flex flex-col">
      <Toaster position="bottom-center" />

      {/* Fixed header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-background/80 border-b border-border">
        {/* Logo row */}
        <div className="h-14 sm:h-16 flex items-center px-6">
          <div className="flex items-center justify-between w-full max-w-5xl mx-auto">
            <a href="#hero">
              <img src={logo} alt="Done[x]" className="h-6 w-auto" />
            </a>
            {/* Nav — desktop only */}
            <nav className="hidden sm:flex items-center gap-6">
              <a
                href="#demo"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                How it works
              </a>
              <button
                type="button"
                onClick={focusEmail}
                className="text-sm font-semibold px-4 py-2 rounded-lg text-primary-foreground"
                style={{
                  backgroundImage: "var(--gradient-primary)",
                  boxShadow: "var(--shadow-elegant)",
                }}
              >
                Start for Free
              </button>
            </nav>
          </div>
        </div>
        {/* Subheader nav — mobile only */}
        <div className="sm:hidden border-t border-border">
          <nav className="flex items-center justify-center gap-6 px-6 py-2.5">
            <a
              href="#demo"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              How it works
            </a>
            <button
              type="button"
              onClick={focusEmail}
              className="text-sm font-semibold px-4 py-1.5 rounded-lg text-primary-foreground"
              style={{
                backgroundImage: "var(--gradient-primary)",
                boxShadow: "var(--shadow-elegant)",
              }}
            >
              Get Early Access
            </button>
          </nav>
        </div>
      </header>

      {/* Ambient blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[480px] w-[480px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 -right-40 h-[420px] w-[420px] rounded-full bg-accent/5 blur-3xl" />
      </div>

      {/* Hero — full viewport height */}
      <section
        id="hero"
        className="relative min-h-screen flex flex-col items-center justify-center px-4 pt-24 sm:pt-16 snap-start"
      >
        <div className="relative w-full max-w-xl space-y-10 text-center">
          {/* Hero copy */}
          <div className="space-y-5">
            <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
              Take action inside Slack —{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "var(--gradient-primary)" }}
              >
                without building a Slack app
              </span>
            </h1>
            <p className="mx-auto max-w-lg text-lg leading-relaxed text-muted-foreground">
              Create forms and actions in minutes, with no OAuth, hosting, or maintenance. Your
              backend gets clean form inputs and simple action payloads — nothing else to manage.
            </p>
            <p className="text-sm text-muted-foreground/70 italic">
              For teams who keep needing a quick approval, request, or internal flow in Slack and
              don't want to stand up an app for each one.
            </p>
          </div>

          {/* Lead capture */}
          <div className="space-y-3">
            <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                disabled={status === "loading"}
                className="flex-1 py-4 text-base"
              />
              <Button
                type="submit"
                disabled={status === "loading"}
                className="py-4 text-base"
                style={{
                  backgroundImage: "var(--gradient-primary)",
                  boxShadow: "var(--shadow-elegant)",
                }}
              >
                {status === "loading" ? "Sending…" : "Get Early Access"}
              </Button>
            </form>
            {status === "error" && (
              <p className="text-sm text-destructive">Something went wrong — please try again.</p>
            )}
            <p className="text-xs text-muted-foreground">Minutes, not a project. No spam.</p>
          </div>
        </div>
      </section>

      {/* Divider — sits at bottom of hero snap zone, not a snap point itself */}
      <div className="w-[80%] mx-auto snap-none">
        <hr className="border-border" />
      </div>

      {/* Interactive demo — full viewport height */}
      <InteractiveDemo />
    </div>
  );
}
